import React, { useMemo, useState } from 'react';
import {
  Copy,
  Filter,
  Plus,
  Search,
  Send,
  Settings2,
  Share2,
  Sparkles,
  Users,
  X,
  MoreHorizontal,
} from 'lucide-react';
import { useToast } from '../../../../components/common/Toast';
import { logDataModels } from '../dataModelsLogger';
import type { DataModel, ModelTable, Space } from '../dataModelsTypes';
import { usePolicyEditorNav, type PolicyEditorMode } from '../governance/policyEditorNav';
import PolicyGraphDialog from '../governance/policyGraph/PolicyGraphDialog';
import { DialogShell, Menu, MenuItem, fieldClass, ghostBtn, iconBtn, primaryBtn, searchClass, surfaces } from './dataModelsUi';
import { emptyPolicy } from '../governance/engine';
import { KIND_POLICY_TYPE } from '../governance/types';
import { launchScope, resolveGovernance, useGovernance, type GovernanceLevel, type PolicyKind, type PolicyStatus, type ResolvedPolicy } from './governanceStore';

type PolicyView = 'effective' | 'inherited' | 'local' | 'overrides' | 'library';
type CatalogPolicy = ResolvedPolicy & { library?: boolean };

const LIBRARY: Array<{ id: string; name: string; description: string; kind: PolicyKind; appliesTo: string }> = [
  { id: 'lib-1', name: 'Standard PII Mask', description: 'Mask email, phone, and national ID', kind: 'Column Mask', appliesTo: 'All assets' },
  { id: 'lib-2', name: 'Region Row Filter', description: 'Limit rows to the caller region', kind: 'Row Filter', appliesTo: 'All models' },
  { id: 'lib-3', name: 'Confidential Classification', description: 'Label finance and HR tables confidential', kind: 'Classification', appliesTo: 'finance.*, hr.*' },
];

const SUGGESTIONS = [
  'Suggest policies for newly detected PII fields',
  'Create a row filter for marketing team',
  'Compare effective policies with the parent WorkSpace',
  'Explain this policy in simple terms',
];

const LEVEL_LABEL: Record<GovernanceLevel, string> = {
  vault: 'WorkSpace',
  model: 'catalog',
  asset: 'schema',
};

export default function CatalogGovernancePanel({
  isLight,
  level,
  space,
  model,
  table,
}: {
  isLight: boolean;
  level: GovernanceLevel;
  space: Space;
  model?: DataModel;
  table?: ModelTable;
}) {
  const ui = surfaces(isLight);
  const muted = ui.muted;
  const scopeName = level === 'asset' ? table?.name ?? 'Schema' : level === 'model' ? model?.name ?? 'Catalog' : space.name;
  const ownerId = level === 'asset' ? table?.id ?? '' : level === 'model' ? model?.id ?? '' : space.id;
  const localLabel = level === 'vault' ? 'This WorkSpace' : level === 'model' ? 'This catalog' : 'This schema';
  const { policies: stored, assets, updatePolicy, removePolicy, activatePolicy, deactivatePolicy, createPolicyDraft } = useGovernance();
  const { showToast } = useToast();
  const { open } = usePolicyEditorNav();
  const openPolicy = (mode: PolicyEditorMode, policyId?: string) => {
    open({
      mode,
      level,
      policyId,
      spaceId: space.id,
      modelId: model?.id,
      tableId: table?.id,
    });
  };
  const [view, setView] = useState<PolicyView>('effective');
  const [query, setQuery] = useState('');
  const [kind, setKind] = useState<'all' | PolicyKind>('all');
  const [selected, setSelected] = useState<string[]>([]);
  const [menuId, setMenuId] = useState<string | null>(null);
  const [copilotOpen, setCopilotOpen] = useState(false);
  const [inherit, setInherit] = useState(true);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<{ id: string; name: string } | null>(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [graphOpen, setGraphOpen] = useState(false);
  const [prompt, setPrompt] = useState('');
  const [draft, setDraft] = useState({
    name: 'Marketing Campaign Access',
    description: 'Allow marketing team members to see only their own campaigns.',
    appliesTo: 'marketing_campaigns',
    condition: 'campaign_owner = current_user_id()',
    audience: 'Marketing Team',
    confidence: 95,
  });
  const [note, setNote] = useState('Suggest a row filter policy for marketing team so they can only see their own campaigns.');

  const policies = useMemo(
    () => resolveGovernance(stored, { level, spaceId: space.id, modelId: model?.id, tableId: table?.id, inherit: true }, assets),
    [stored, assets, level, space.id, model?.id, table?.id],
  );

  const counts = useMemo(() => {
    const inherited = policies.filter((policy) => !policy.ownedHere).length;
    const local = policies.filter((policy) => policy.ownedHere && policy.flag !== 'override').length;
    const overrides = policies.filter((policy) => policy.ownedHere && policy.flag === 'override').length;
    return { total: policies.length, inherited, local, overrides };
  }, [policies]);

  const visible = useMemo(() => {
    if (view === 'library') return LIBRARY.map((item) => libraryRow(item, level, ownerId));
    return policies.filter((policy) => {
      if (view === 'inherited' && policy.ownedHere) return false;
      if (view === 'local' && (!policy.ownedHere || policy.flag === 'override')) return false;
      if (view === 'overrides' && !(policy.ownedHere && policy.flag === 'override')) return false;
      if (view === 'effective' && !inherit && !policy.ownedHere && policy.flag !== 'mandatory') return false;
      if (kind !== 'all' && policy.kind !== kind) return false;
      const haystack = `${policy.name} ${policy.description} ${policy.appliesTo}`.toLowerCase();
      return haystack.includes(query.trim().toLowerCase());
    });
  }, [inherit, kind, level, ownerId, policies, query, view]);

  const allChecked = visible.length > 0 && visible.every((policy) => selected.includes(policy.id));

  const toggleAll = () => {
    setSelected(allChecked ? [] : visible.map((policy) => policy.id));
  };

  const applySuggestion = (text: string) => {
    setNote(text);
    if (/row filter/i.test(text)) {
      setDraft({
        name: 'Marketing Campaign Access',
        description: 'Allow marketing team members to see only their own campaigns.',
        appliesTo: 'marketing_campaigns',
        condition: 'campaign_owner = current_user_id()',
        audience: 'Marketing Team',
        confidence: 95,
      });
    } else if (/pii/i.test(text)) {
      setDraft({
        name: 'Detected PII Mask',
        description: 'Mask newly detected email, phone, and address columns for non-privileged roles.',
        appliesTo: 'customer.*',
        condition: "role NOT IN ('owner', 'data_steward')",
        audience: 'Everyone except stewards',
        confidence: 91,
      });
    } else if (/compare/i.test(text)) {
      setDraft({
        name: 'Parent WorkSpace comparison',
        description: model
          ? `${model.name} inherits ${counts.inherited} policies from ${space.name}. ${counts.overrides} model overrides are in effect.`
          : `${space.name} has ${counts.inherited} WorkSpace policies. ${counts.overrides} overrides are in effect.`,
        appliesTo: scopeName,
        condition: 'inherited AND NOT overridden',
        audience: space.name,
        confidence: 88,
      });
    } else {
      setDraft({
        name: 'Policy explained',
        description: 'Effective policy is the WorkSpace rule, then any model override. Mandatory WorkSpace policies cannot be turned off.',
        appliesTo: 'All assets',
        condition: 'space.mandatory OR catalog.override OR space.inherited',
        audience: 'Model readers',
        confidence: 86,
      });
    }
    logDataModels('app', 'governance_suggestion_applied', { level, ownerId, text });
  };

  const saveHere = (input: { name: string; description: string; kind: PolicyKind; appliesTo: string }) => {
    const draft = emptyPolicy(launchScope(level, space.id, model?.id, table?.id), KIND_POLICY_TYPE[input.kind]);
    draft.name = input.name;
    draft.description = input.description;
    void createPolicyDraft(draft)
      .then(() => setView('local'))
      .catch((error: unknown) => {
        logDataModels('error', 'governance_policy_add_failed', {
          level,
          ownerId,
          message: error instanceof Error ? error.message : 'failed',
        });
      });
  };

  const addDraft = () => {
    saveHere({ name: draft.name, description: draft.description, kind: 'Row Filter', appliesTo: draft.appliesTo });
  };

  const confirmRemovePolicy = () => {
    if (!pendingDelete || deleteBusy) return;
    const { id } = pendingDelete;
    setDeleteBusy(true);
    void removePolicy(id)
      .then((synced) => {
        showToast({
          type: synced ? 'success' : 'error',
          message: synced
            ? 'Policy deleted and removed from policy server'
            : 'Policy deleted. Policy server sync failed.',
        });
        setPendingDelete(null);
      })
      .catch((error: unknown) => {
        const message = error instanceof Error ? error.message : 'failed';
        logDataModels('error', 'governance_policy_remove_failed', { id, message });
        showToast({
          type: 'error',
          message: error instanceof Error ? error.message : 'Unable to delete policy',
        });
      })
      .finally(() => setDeleteBusy(false));
  };

  const panel = (
    <div className={`grid gap-3 ${copilotOpen ? 'xl:grid-cols-[minmax(0,1fr)_260px]' : ''}`}>
      <div className="min-w-0">
        <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
          <p className={`text-xs ${muted}`}>
            {counts.total} policies · {counts.inherited} inherited · {counts.local} on this {LEVEL_LABEL[level]}
          </p>
          <div className="flex items-center gap-2">
            <button type="button" className={`${ghostBtn(isLight)} !h-7 !px-2.5 !text-xs`} onClick={() => setCopilotOpen((open) => !open)}>
              <Sparkles size={13} />
              Ask
            </button>
            <button type="button" className={`${ghostBtn(isLight)} !h-7 !px-2.5 !text-xs`} onClick={() => setSettingsOpen(true)}>
              <Settings2 size={13} />
              Inheritance
            </button>
            <button type="button" className={`${ghostBtn(isLight)} !h-7 !px-2.5 !text-xs`} onClick={() => setGraphOpen(true)}>
              <Share2 size={13} />
              View Policy Graph
            </button>
            {view !== 'library' ? (
              <button type="button" className={primaryBtn('!h-7 !px-2.5 !text-xs')} onClick={() => openPolicy('create')}>
                <Plus size={13} />
                Create
              </button>
            ) : null}
          </div>
        </div>

        <div className={`flex gap-1 overflow-x-auto border-b ${ui.border}`}>
          <ViewTab active={view === 'effective'} isLight={isLight} label="Effective" onClick={() => setView('effective')} />
          <ViewTab active={view === 'inherited'} isLight={isLight} label="Inherited" count={counts.inherited} onClick={() => setView('inherited')} />
          <ViewTab active={view === 'local'} isLight={isLight} label={localLabel} count={counts.local} onClick={() => setView('local')} />
          <ViewTab active={view === 'overrides'} isLight={isLight} label="Overrides" count={counts.overrides} onClick={() => setView('overrides')} />
          <ViewTab active={view === 'library'} isLight={isLight} label="Library" onClick={() => setView('library')} />
        </div>

        <div className="mt-2 flex flex-wrap items-center gap-2">
          <div className="relative min-w-[200px] flex-1">
            <Search size={13} className={`absolute left-2.5 top-1/2 -translate-y-1/2 ${muted}`} />
            <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Filter policies" className={`${searchClass(isLight)} !h-7 !text-xs`} />
          </div>
          <FilterSelect isLight={isLight} value={kind} onChange={(value) => setKind(value as 'all' | PolicyKind)} options={['all', 'Row Filter', 'Column Mask', 'Data Access', 'Time Access', 'Classification']} labels={['All types', 'Row filter', 'Column mask', 'Data access', 'Time access', 'Classification']} />
        </div>

        <div className={`mt-2 min-h-[calc(100dvh-12rem)] overflow-hidden rounded-md border ${ui.card}`}>
          <div className="min-h-[calc(100dvh-12rem)] overflow-x-auto">
            <table className="min-w-full text-xs">
              <thead>
                <tr className={`text-left text-[11px] font-medium ${ui.tableHead}`}>
                  <th className="w-8 px-2.5 py-1.5">
                    <input type="checkbox" checked={allChecked} onChange={toggleAll} aria-label="Select all policies" />
                  </th>
                  <th className="px-2.5 py-1.5">Policy</th>
                  <th className="px-2.5 py-1.5">Type</th>
                  <th className="px-2.5 py-1.5">Source</th>
                  <th className="px-2.5 py-1.5">Applies to</th>
                  <th className="px-2.5 py-1.5">Status</th>
                  <th className="w-10 px-2.5 py-1.5" />
                </tr>
              </thead>
              <tbody>
                {visible.map((policy) => (
                  <tr key={policy.id} className={`border-t ${ui.border} ${policy.library ? '' : isLight ? 'cursor-pointer hover:bg-[#F7F9FB]' : 'cursor-pointer hover:bg-white/[0.03]'}`} onClick={() => { if (!policy.library) openPolicy('view', policy.id); }}>
                    <td className="px-2.5 py-1.5" onClick={(event) => event.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={selected.includes(policy.id)}
                        onChange={() => setSelected((current) => current.includes(policy.id) ? current.filter((id) => id !== policy.id) : [...current, policy.id])}
                        aria-label={`Select ${policy.name}`}
                      />
                    </td>
                    <td className="px-2.5 py-1.5">
                      <p className={`font-medium ${isLight ? 'text-[#1C1C1C]' : 'text-white'}`}>{policy.name}</p>
                      <p className={`text-[11px] ${muted}`}>{policy.description}</p>
                    </td>
                    <td className={`px-2.5 py-1.5 ${muted}`}>{policy.kind}</td>
                    <td className="px-2.5 py-1.5"><SourceBadge policy={policy} isLight={isLight} /></td>
                    <td className={`px-2.5 py-1.5 font-mono text-[11px] ${muted}`}>{policy.appliesTo}</td>
                    <td className="px-2.5 py-1.5"><StatusBadge status={policy.status} isLight={isLight} /></td>
                    <td className="px-2.5 py-1.5" onClick={(event) => event.stopPropagation()}>
                      <div className="relative">
                        <button type="button" className={iconBtn(isLight)} aria-label={`Actions for ${policy.name}`} onClick={(event) => { event.stopPropagation(); setMenuId(menuId === policy.id ? null : policy.id); }}>
                          <MoreHorizontal size={14} />
                        </button>
                        <Menu open={menuId === policy.id} onClose={() => setMenuId(null)} isLight={isLight}>
                          <MenuItem isLight={isLight} onClick={() => { setMenuId(null); applySuggestion(`Explain ${policy.name} in simple terms`); setCopilotOpen(true); }}>Explain</MenuItem>
                          {policy.library ? (
                            <MenuItem isLight={isLight} onClick={() => { setMenuId(null); saveHere({ name: policy.name, description: policy.description, kind: policy.kind, appliesTo: policy.appliesTo }); }}>Add here</MenuItem>
                          ) : policy.ownedHere ? (
                            <>
                              <MenuItem isLight={isLight} onClick={() => { setMenuId(null); openPolicy('view', policy.id); }}>View</MenuItem>
                              <MenuItem isLight={isLight} onClick={() => { setMenuId(null); openPolicy('edit', policy.id); }}>Edit</MenuItem>
                              {policy.status !== 'Enabled' ? (
                                <MenuItem isLight={isLight} onClick={() => {
                                  setMenuId(null);
                                  void (policy.detail
                                    ? activatePolicy({ ...policy.detail, status: 'ACTIVE' })
                                    : updatePolicy(policy.id, { status: 'Enabled', updated: 'Just now' })
                                  ).catch((error: unknown) => {
                                    logDataModels('error', 'governance_policy_enable_failed', { id: policy.id, message: error instanceof Error ? error.message : 'failed' });
                                  });
                                }}>Enable</MenuItem>
                              ) : (
                                <MenuItem isLight={isLight} onClick={() => {
                                  setMenuId(null);
                                  void (policy.detail
                                    ? deactivatePolicy({ ...policy.detail, status: 'INACTIVE' })
                                    : updatePolicy(policy.id, { status: 'Disabled', updated: 'Just now' })
                                  ).catch((error: unknown) => {
                                    logDataModels('error', 'governance_policy_disable_failed', { id: policy.id, message: error instanceof Error ? error.message : 'failed' });
                                  });
                                }}>Disable</MenuItem>
                              )}
                              <MenuItem isLight={isLight} onClick={() => {
                                setMenuId(null);
                                setPendingDelete({ id: policy.id, name: policy.name });
                              }}>Remove</MenuItem>
                            </>
                          ) : (
                            <MenuItem isLight={isLight} onClick={() => { setMenuId(null); openPolicy('view', policy.id); }}>View inherited</MenuItem>
                          )}
                        </Menu>
                      </div>
                    </td>
                  </tr>
                ))}
                {!visible.length ? (
                  <tr>
                    <td colSpan={7} className={`px-2.5 py-10 text-center text-xs ${muted}`}>
                      {view === 'library'
                        ? 'No library policies match this filter.'
                        : policies.length
                          ? 'No policies match this view.'
                          : `No policies on this ${LEVEL_LABEL[level]} yet.`}
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {copilotOpen ? (
        <aside className={`xl:sticky xl:top-0 h-fit rounded-xl border p-3 ${ui.card}`}>
          <div className="flex items-start justify-between gap-2">
            <div>
              <div className="flex items-center gap-2">
                <h3 className={`text-xs font-semibold ${isLight ? 'text-slate-900' : 'text-white'}`}>Policy Copilot</h3>
                <span className={`rounded px-1.5 py-0.5 text-[10px] font-medium ${isLight ? 'bg-[#F2F4F7] text-[#3C4048]' : 'bg-white/10 text-slate-200'}`}>Preview</span>
              </div>
              <p className={`mt-1 text-xs leading-5 ${muted}`}>Get AI-powered help to create, optimize, and manage governance policies for this {LEVEL_LABEL[level]}.</p>
            </div>
            <button type="button" className={ghostBtn(isLight)} aria-label="Close copilot" onClick={() => setCopilotOpen(false)}>
              <X size={14} />
            </button>
          </div>

          <div className={`mt-4 rounded-md border p-3 ${isLight ? 'border-[#E6E8EB] bg-[#F7F8FA]' : 'border-white/10 bg-white/5'}`}>
            <p className={`inline-flex items-center gap-1.5 text-sm font-medium ${isLight ? 'text-[#1C1C1C]' : 'text-white'}`}>
              <Sparkles size={14} />
              Understand your data
            </p>
            <p className={`mt-1 text-xs ${muted}`}>Ask questions, get recommendations, or generate policies using natural language.</p>
          </div>

          <div className="mt-3 space-y-2">
            {SUGGESTIONS.map((item) => (
              <button key={item} type="button" onClick={() => applySuggestion(item)} className={`flex w-full items-center gap-2 rounded-md border px-3 py-2 text-left text-xs ${isLight ? 'border-[#E6E8EB] text-[#3C4048] hover:bg-[#F7F8FA]' : 'border-white/10 text-slate-200 hover:bg-white/5'}`}>
                <Sparkles size={12} className={muted} />
                {item}
              </button>
            ))}
          </div>

          <div className={`mt-4 rounded-xl px-3 py-2 text-xs leading-5 ${isLight ? 'bg-slate-100 text-slate-600' : 'bg-white/5 text-slate-300'}`}>{note}</div>

          <div className={`mt-3 rounded-2xl border p-3 ${isLight ? 'border-slate-200' : 'border-indigo-400/15'}`}>
            <div className="flex items-center justify-between gap-2">
              <p className={`inline-flex items-center gap-1.5 text-sm font-medium ${isLight ? 'text-slate-900' : 'text-white'}`}>
                <span className={`inline-flex h-6 w-6 items-center justify-center rounded-md ${isLight ? 'bg-[#F2F4F7] text-[#4B5563]' : 'bg-white/10 text-slate-300'}`}><Filter size={12} /></span>
                {draft.name}
              </p>
              <span className={`rounded px-1.5 py-0.5 text-[10px] font-medium ${isLight ? 'bg-[#F2F4F7] text-[#3C4048]' : 'bg-white/10 text-slate-200'}`}>Draft</span>
            </div>
            <p className={`mt-2 text-xs ${muted}`}>{draft.description}</p>
            <p className={`mt-3 text-[11px] font-medium uppercase tracking-wide ${muted}`}>Applies to</p>
            <p className={`mt-1 font-mono text-xs ${isLight ? 'text-slate-800' : 'text-slate-100'}`}>{draft.appliesTo}</p>
            <p className={`mt-3 text-[11px] font-medium uppercase tracking-wide ${muted}`}>Filter condition</p>
            <div className={`mt-1 flex items-center justify-between gap-2 rounded-lg px-2 py-1.5 font-mono text-[11px] ${isLight ? 'bg-slate-100 text-slate-700' : 'bg-black text-slate-200'}`}>
              <span className="truncate">{draft.condition}</span>
              <button type="button" aria-label="Copy condition" onClick={() => navigator.clipboard?.writeText(draft.condition)} className={muted}>
                <Copy size={12} />
              </button>
            </div>
            <p className={`mt-3 text-[11px] font-medium uppercase tracking-wide ${muted}`}>Users / Groups</p>
            <p className={`mt-1 inline-flex items-center gap-1.5 text-xs ${isLight ? 'text-slate-800' : 'text-slate-100'}`}><Users size={12} />{draft.audience}</p>
            <div className="mt-3">
              <div className="flex items-center justify-between text-[11px]">
                <span className={muted}>Confidence</span>
                <span className={isLight ? 'font-medium text-[#1C1C1C]' : 'font-medium text-white'}>{draft.confidence}%</span>
              </div>
              <div className={`mt-1 h-1.5 overflow-hidden rounded-full ${isLight ? 'bg-slate-100' : 'bg-white/10'}`}>
                <div className={`h-full rounded-full ${isLight ? 'bg-[#1B6CA8]' : 'bg-sky-400'}`} style={{ width: `${draft.confidence}%` }} />
              </div>
            </div>
            <div className="mt-3 flex items-center gap-2">
              <button type="button" className={primaryBtn('flex-1')} onClick={addDraft}>Add policy</button>
              <button
                type="button"
                className={ghostBtn(isLight)}
                onClick={() => setDraft((current) => ({ ...current, condition: current.condition.includes('AND') ? current.condition : `${current.condition} AND region = caller_region()` }))}
              >
                Refine
              </button>
            </div>
          </div>

          <form
            className="mt-3 flex items-center gap-2"
            onSubmit={(event) => {
              event.preventDefault();
              if (!prompt.trim()) return;
              applySuggestion(prompt.trim());
              setPrompt('');
            }}
          >
            <input value={prompt} onChange={(event) => setPrompt(event.target.value)} placeholder="Ask anything about policies..." className={fieldClass(isLight)} />
            <button type="submit" className={primaryBtn('px-2.5')} aria-label="Send">
              <Send size={14} />
            </button>
          </form>
        </aside>
      ) : null}

      {graphOpen ? (
        <PolicyGraphDialog
          level={level}
          spaceId={space.id}
          modelId={model?.id}
          tableId={table?.id}
          onClose={() => setGraphOpen(false)}
        />
      ) : null}

      {settingsOpen ? (
        <DialogShell
          title="Inheritance"
          subtitle={scopeName}
          isLight={isLight}
          onClose={() => setSettingsOpen(false)}
          footer={<button type="button" className={primaryBtn()} onClick={() => setSettingsOpen(false)}>Done</button>}
        >
          {level === 'vault' ? null : (
            <label className="flex items-center justify-between gap-3 text-sm">
              <span className={isLight ? 'text-slate-800' : 'text-slate-100'}>Inherit parent policies</span>
              <input type="checkbox" checked={inherit} onChange={(event) => setInherit(event.target.checked)} />
            </label>
          )}
          <p className={`text-xs ${muted}`}>
            {level === 'vault'
              ? 'Policies on this WorkSpace apply to every catalog and schema inside it. A change here updates those children.'
              : level === 'model'
                ? 'This catalog inherits WorkSpace policies. WorkSpace changes show up here and on its schemas. Policies added here apply to those schemas.'
                : 'This schema inherits WorkSpace and catalog policies. Parent changes show up here.'}
          </p>
        </DialogShell>
      ) : null}

      {pendingDelete ? (
        <DialogShell
          title="Remove policy"
          subtitle={`${pendingDelete.name} will be deleted from this tenant and the policy server.`}
          isLight={isLight}
          onClose={() => {
            if (!deleteBusy) setPendingDelete(null);
          }}
          footer={
            <>
              <button type="button" className={ghostBtn(isLight)} disabled={deleteBusy} onClick={() => setPendingDelete(null)}>Cancel</button>
              <button type="button" className={primaryBtn()} disabled={deleteBusy} onClick={confirmRemovePolicy}>
                Remove
              </button>
            </>
          }
        >
          <p className={`text-sm ${isLight ? 'text-[#3C4048]' : 'text-slate-300'}`}>
            This permanently deletes the policy. It cannot be undone.
          </p>
        </DialogShell>
      ) : null}

    </div>
  );

  return panel;
}

function summarize(policies: CatalogPolicy[]) {
  const ofKind = (kind: PolicyKind) => policies.filter((policy) => policy.kind === kind);
  const split = (rows: CatalogPolicy[]) => ({
    total: rows.length,
    inherited: rows.filter((policy) => !policy.ownedHere).length,
    catalog: rows.filter((policy) => policy.ownedHere).length,
  });
  const masks = split(ofKind('Column Mask'));
  const filters = split(ofKind('Row Filter'));
  const classes = split(ofKind('Classification'));
  const access = ofKind('Data Access').length;
  return {
    total: policies.length,
    inherited: policies.filter((policy) => !policy.ownedHere).length,
    catalog: policies.filter((policy) => policy.ownedHere && policy.flag !== 'override').length,
    overrides: policies.filter((policy) => policy.ownedHere && policy.flag === 'override').length,
    masks: masks.total,
    maskInherited: masks.inherited,
    maskCatalog: masks.catalog,
    filters: filters.total,
    filterInherited: filters.inherited,
    filterCatalog: filters.catalog,
    classes: classes.total,
    classInherited: classes.inherited,
    classCatalog: classes.catalog,
    access,
  };
}

export function summarizePolicies(policies: CatalogPolicy[]) {
  return summarize(policies);
}

function libraryRow(
  item: { id: string; name: string; description: string; kind: PolicyKind; appliesTo: string },
  level: GovernanceLevel,
  ownerId: string,
): CatalogPolicy {
  return {
    ...item,
    level,
    ownerId,
    flag: 'standard',
    status: 'Draft',
    updated: 'Template',
    actor: 'Policy Library',
    ownedHere: false,
    library: true,
  };
}

function ViewTab({ active, label, count, isLight, onClick }: { active: boolean; label: string; count?: number; isLight: boolean; onClick: () => void }) {
  return (
    <button type="button" onClick={onClick} className={`relative shrink-0 px-2.5 py-1.5 text-xs ${active ? (isLight ? 'font-semibold text-[#1C1C1C]' : 'font-semibold text-white') : isLight ? 'text-[#6B7280]' : 'text-slate-400'}`}>
      {label}
      {count !== undefined ? <span className={`ml-1.5 text-[11px] ${isLight ? 'text-[#6B7280]' : 'text-slate-400'}`}>{count}</span> : null}
      {active ? <span className="absolute inset-x-2 bottom-0 h-0.5 bg-[#1B6CA8]" /> : null}
    </button>
  );
}

function SourceBadge({ policy, isLight }: { policy: CatalogPolicy; isLight: boolean }) {
  const flag = policy.library ? null : !policy.ownedHere ? 'Inherited' : policy.flag === 'mandatory' ? 'Mandatory' : policy.flag === 'override' ? 'Override' : null;
  const flagTone = isLight ? 'text-[#6B7280]' : 'text-slate-400';
  const source = policy.level === 'vault' ? 'WorkSpace' : policy.level === 'model' ? 'Catalog' : 'Schema';
  return (
    <div>
      <p className={`text-xs font-medium ${isLight ? 'text-slate-800' : 'text-slate-100'}`}>{source}</p>
      {flag ? <p className={`text-[11px] ${flagTone}`}>{policy.flag === 'mandatory' || policy.flag === 'override' ? `*${flag}` : flag}</p> : null}
    </div>
  );
}

function StatusBadge({ status, isLight }: { status: PolicyStatus; isLight: boolean }) {
  const tone = status === 'Enabled'
    ? isLight ? 'bg-emerald-50 text-emerald-700' : 'bg-emerald-500/15 text-emerald-200'
    : status === 'Disabled'
      ? isLight ? 'bg-amber-50 text-amber-700' : 'bg-amber-500/15 text-amber-200'
      : isLight ? 'bg-[#F2F4F7] text-[#3C4048]' : 'bg-white/10 text-slate-200';
  return <span className={`inline-flex rounded px-1.5 py-0.5 text-[11px] font-medium ${tone}`}>{status}</span>;
}

function FilterSelect({
  isLight,
  value,
  onChange,
  options,
  labels,
}: {
  isLight: boolean;
  value: string;
  onChange: (value: string) => void;
  options: string[];
  labels: string[];
}) {
  return (
    <select
      value={value}
      onChange={(event) => onChange(event.target.value)}
      className={isLight
        ? 'h-7 rounded-md border border-slate-200 bg-white px-2.5 text-xs text-slate-700 focus:border-indigo-400 focus:outline-none'
        : 'h-7 rounded-md border border-indigo-400/20 bg-black px-2.5 text-xs text-slate-200 focus:border-indigo-400 focus:outline-none'}
    >
      {options.map((option, index) => (
        <option key={option} value={option}>{labels[index]}</option>
      ))}
    </select>
  );
}

